import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/atoms/Card';

import type { WizardDestination } from '../../operateWizardReducer';

import styles from '../../operateModals.module.scss';

interface Props {
    destinations: WizardDestination[];
    cageNameById: Map<number, string>;
    /** Active bioscope ids per cage; an empty list is shown as "no bioscope". */
    cageBioscopesById: Map<number, number[]>;
    sourceNameById: Map<number, string>;
    /** The source's own cage in split mode, whose destination is tagged as the remainder. */
    remainderCageId: number | null;
}

export function ConfirmDestinationsSection(props: Props) {
    const { destinations, cageNameById, cageBioscopesById, sourceNameById, remainderCageId } =
        props;
    const { t } = useTranslation('fishGroups');

    return (
        <Card as="article" radius="sm" padding="xs" className={styles.confirmSection}>
            <h5 className={styles.confirmSectionTitle}>
                {t('actions.operate.confirm.destinationsSection', {
                    count: destinations.length,
                })}
            </h5>
            <ul className={styles.summaryList}>
                {destinations.map((d) => {
                    const cageName = d.cageId != null ? cageNameById.get(d.cageId) : '';
                    const bioscopes =
                        d.cageId != null ? (cageBioscopesById.get(d.cageId) ?? []) : [];
                    const bioscopeText =
                        bioscopes.length === 0
                            ? t('actions.operate.confirm.noBioscope')
                            : t('actions.operate.confirm.bioscopeInfo', {
                                  ids: bioscopes.join(', '),
                                  count: bioscopes.length,
                              });
                    const inflowNames = d.receivesFrom
                        .map((i) => sourceNameById.get(i.sourceFishGroupId) ?? '')
                        .filter(Boolean);
                    const isRemainder = remainderCageId !== null && d.cageId === remainderCageId;
                    return (
                        <li key={d.tempId}>
                            <span className={styles.summaryName}>
                                {d.name || t('actions.operate.confirm.unnamedDestination')}
                            </span>
                            {isRemainder && (
                                <Badge tone="accent" shape="tag">
                                    {t('actions.operate.confirm.remainderTag')}
                                </Badge>
                            )}
                            {cageName && (
                                <span className={styles.summaryMeta}>
                                    → {cageName} ({bioscopeText})
                                </span>
                            )}
                            {inflowNames.length > 0 && (
                                <span className={styles.summaryInflows}>
                                    {t('actions.operate.confirm.receivesFromLabel', {
                                        sources: inflowNames.join(', '),
                                    })}
                                </span>
                            )}
                            {d.estimatedFishCount.trim() !== '' && (
                                <span className={styles.summaryMeta}>
                                    {t('actions.operate.confirm.estimatedCount', {
                                        value: d.estimatedFishCount,
                                    })}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </Card>
    );
}
